"use client"

import React from 'react'
import { Button } from "@/components/ui/button"
import { useLanguage } from "@/contexts/language-context"
import type { NavigationMode } from "@/types/ui/engagement-view"
import { MoreVertical } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ENGAGEMENT_ACTIONS, getActionLabel, getActionLoadingLabel, type EngagementActionCode } from "@/lib/constants/engagement-actions"

export interface ActionConfig {
  canExecute: boolean
  handler: () => void | Promise<void>
}

export interface EngagementActionsProps {
  isHandlingAction: boolean
  currentAction?: string
  allSectionsComplete: boolean
  navigationMode: NavigationMode
  recordId?: number
  onShowHistory?: () => void
  
  actions: {
    refresh: ActionConfig
    save: ActionConfig
    submit: ActionConfig
    submitForReview: ActionConfig
    discard: ActionConfig
    approve: ActionConfig
    decline: ActionConfig
    createChangeRequest: ActionConfig
    recall: ActionConfig
    recallToAccounting: ActionConfig
    complete: ActionConfig
    reactivate: ActionConfig
  }
}

export function EngagementActions({
  isHandlingAction,
  currentAction,
  navigationMode,
  recordId,
  onShowHistory,
  actions,
}: EngagementActionsProps) {
  const { dictionary } = useLanguage()

  // Helper to get action label (loading state or normal state)
  const getLabel = (actionCode: EngagementActionCode) => {
    if (currentAction === actionCode) {
      return getActionLoadingLabel(actionCode, dictionary)
    }
    return getActionLabel(actionCode, dictionary)
  }

  // Don't render if no actions available (including history)
  const hasActions = Object.values(actions).some(action => action.canExecute) || (recordId && onShowHistory)
  if (!hasActions) {
    return null
  }

  return (
    <>
      {/* Save button */}
      {actions.save.canExecute && (
        <Button 
          onClick={actions.save.handler}
          disabled={isHandlingAction}
          variant="outline"
          size="sm"
          className="rounded-full"
        >
          {getLabel(ENGAGEMENT_ACTIONS.SAVE)}
        </Button>
      )}

      {/* Submit for Review button */}
      {actions.submit.canExecute && (
        <Button 
          onClick={actions.submit.handler}
          disabled={isHandlingAction}
          size="sm"
          className="rounded-full"
        >
          {getLabel(ENGAGEMENT_ACTIONS.SUBMIT)}
        </Button>
      )}

      {/* Submit for Review button (new action) */}
      {actions.submitForReview.canExecute && (
        <Button 
          onClick={actions.submitForReview.handler}
          disabled={isHandlingAction}
          size="sm"
          className="rounded-full"
        >
          {getLabel(ENGAGEMENT_ACTIONS.SUBMIT_FOR_REVIEW)}
        </Button>
      )}

      {/* Decline button */}
      {actions.decline.canExecute && (
        <Button 
          variant="outline"
          onClick={actions.decline.handler}
          disabled={isHandlingAction}
          size="sm"
          className="rounded-full"
        >
          {getLabel(ENGAGEMENT_ACTIONS.DECLINE)}
        </Button>
      )}

      {/* Approve button */}
      {actions.approve.canExecute && (
        <Button 
          onClick={actions.approve.handler}
          disabled={isHandlingAction}
          size="sm"
          className="rounded-full"
        >
          {getLabel(ENGAGEMENT_ACTIONS.APPROVE)}
        </Button>
      )}

      {/* Kebab Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVertical className="h-4 w-4" />
            <span className="sr-only">More actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={navigationMode === "setup" ? "end" : "start"}>
          {actions.createChangeRequest.canExecute && (
            <DropdownMenuItem 
              onClick={() => actions.createChangeRequest.handler()}
              disabled={isHandlingAction}
            >
              {getLabel(ENGAGEMENT_ACTIONS.CREATE_CHANGE_REQUEST)}
            </DropdownMenuItem>
          )}
          
          {actions.refresh.canExecute && (
            <DropdownMenuItem 
              onClick={() => actions.refresh.handler()}
              disabled={isHandlingAction}
            >
              {getLabel(ENGAGEMENT_ACTIONS.REFRESH)}
            </DropdownMenuItem>
          )}
          
          {actions.discard.canExecute && (
            <DropdownMenuItem 
              onClick={() => actions.discard.handler()}
              disabled={isHandlingAction}
              className="text-destructive focus:text-destructive"
            >
              {getLabel(ENGAGEMENT_ACTIONS.DISCARD)}
            </DropdownMenuItem>
          )}
          
          {actions.recall.canExecute && (
            <DropdownMenuItem 
              onClick={() => actions.recall.handler()}
              disabled={isHandlingAction}
            >
              {getLabel(ENGAGEMENT_ACTIONS.RECALL)}
            </DropdownMenuItem>
          )}
          
          {actions.recallToAccounting.canExecute && (
            <DropdownMenuItem 
              onClick={() => actions.recallToAccounting.handler()}
              disabled={isHandlingAction}
            >
              {getLabel(ENGAGEMENT_ACTIONS.RECALL_TO_ACCOUNTING)}
            </DropdownMenuItem>
          )}
          
          {actions.complete.canExecute && (
            <DropdownMenuItem 
              onClick={() => actions.complete.handler()}
              disabled={isHandlingAction}
            >
              {getLabel(ENGAGEMENT_ACTIONS.COMPLETE)}
            </DropdownMenuItem>
          )}
          
          {actions.reactivate.canExecute && (
            <DropdownMenuItem 
              onClick={() => actions.reactivate.handler()}
              disabled={isHandlingAction}
            >
              {getLabel(ENGAGEMENT_ACTIONS.REACTIVATE)}
            </DropdownMenuItem>
          )}
          
          {recordId && onShowHistory && (
            <DropdownMenuItem onClick={onShowHistory}>
              History
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

